import { useEffect, useState } from "react";

interface WeatherData {
  temp: string;
  feelsLike: string;
  desc: string;
  humidity: string;
  windSpeed: string;
  icon: string;
}

const weatherIcons: Record<string, string> = {
  sunny: "ri-sun-line",
  cloudy: "ri-cloudy-line",
  rainy: "ri-rainy-line",
  snowy: "ri-snowy-line",
  thunder: "ri-thunderstorms-line",
  fog: "ri-mist-line",
  partly: "ri-sun-cloudy-line",
};

function getWeatherIcon(desc: string): string {
  const d = desc.toLowerCase();
  if (d.includes("rain") || d.includes("yağmur") || d.includes("drizzle")) return weatherIcons.rainy;
  if (d.includes("snow") || d.includes("kar")) return weatherIcons.snowy;
  if (d.includes("thunder") || d.includes("gök")) return weatherIcons.thunder;
  if (d.includes("fog") || d.includes("mist") || d.includes("sis")) return weatherIcons.fog;
  if (d.includes("overcast") || d.includes("bulutlu")) return weatherIcons.cloudy;
  if (d.includes("cloud") || d.includes("partly")) return weatherIcons.partly;
  return weatherIcons.sunny;
}

const DAYS_TR = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

interface DayForecast {
  day: string;
  max: string;
  min: string;
  desc: string;
}

export default function WeatherSection() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [forecast, setForecast] = useState<DayForecast[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    fetch("https://wttr.in/Bursa?format=j1", { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        const current = data.current_condition?.[0];
        if (!current) return;
        setWeather({
          temp: current.temp_C,
          feelsLike: current.FeelsLikeC,
          desc: current.lang_tr?.[0]?.value ?? current.weatherDesc?.[0]?.value ?? "",
          humidity: current.humidity,
          windSpeed: current.windspeedKmph,
          icon: getWeatherIcon(current.weatherDesc?.[0]?.value ?? ""),
        });

        const days: DayForecast[] = (data.weather ?? []).slice(0, 5).map((d: Record<string, unknown>) => {
          const dateStr = d.date as string;
          const date = new Date(dateStr);
          const descArr = d.hourly as Array<Record<string, Array<Record<string, string>>>>;
          const midDesc = descArr?.[4]?.lang_tr?.[0]?.value ?? descArr?.[4]?.weatherDesc?.[0]?.value ?? "";
          return {
            day: DAYS_TR[date.getDay()],
            max: d.maxtempC as string,
            min: d.mintempC as string,
            desc: midDesc,
          };
        });
        setForecast(days);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  if (loading) {
    return (
      <section className="py-10 bg-gradient-to-br from-stone-50 to-stone-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="h-40 bg-stone-200 rounded-2xl animate-pulse" />
        </div>
      </section>
    );
  }

  if (!weather) return null;

  return (
    <section className="py-12 bg-gradient-to-br from-stone-50 to-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-2">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 tracking-tight">Bursa Hava Durumu</h2>
            <p className="text-stone-500 text-sm mt-1">Güncel ve 5 günlük tahmin</p>
          </div>
          <span className="text-xs text-stone-400 flex items-center gap-1">
            <i className="ri-refresh-line" /> wttr.in kaynaklı
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Current Weather */}
          <div className="md:col-span-1 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Şu an • Bursa</p>
                <div className="text-6xl font-bold mt-1">{weather.temp}°</div>
                <p className="text-emerald-100 mt-2 text-sm capitalize">{weather.desc}</p>
              </div>
              <div className="w-16 h-16 flex items-center justify-center">
                <i className={`${weather.icon} text-5xl text-emerald-100`} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-emerald-500/40">
              <div className="text-center">
                <p className="text-emerald-200 text-xs">Hissedilen</p>
                <p className="text-white font-semibold text-sm">{weather.feelsLike}°</p>
              </div>
              <div className="text-center">
                <p className="text-emerald-200 text-xs">Nem</p>
                <p className="text-white font-semibold text-sm">%{weather.humidity}</p>
              </div>
              <div className="text-center">
                <p className="text-emerald-200 text-xs">Rüzgar</p>
                <p className="text-white font-semibold text-sm">{weather.windSpeed} km/h</p>
              </div>
            </div>
          </div>

          {/* Forecast */}
          <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {forecast.map((day, i) => (
              <div
                key={i}
                className={`rounded-xl p-4 flex flex-col items-center gap-2 border transition-all ${
                  i === 0
                    ? "bg-stone-800 border-stone-700 text-white"
                    : "bg-white border-stone-100 text-stone-700 hover:border-emerald-200"
                }`}
              >
                <p className={`text-xs font-semibold ${i === 0 ? "text-stone-300" : "text-stone-400"}`}>
                  {i === 0 ? "Bugün" : day.day}
                </p>
                <i className={`${getWeatherIcon(day.desc)} text-2xl ${i === 0 ? "text-emerald-400" : "text-stone-400"}`} />
                <div className="text-center">
                  <p className="font-bold text-sm">{day.max}°</p>
                  <p className={`text-xs ${i === 0 ? "text-stone-400" : "text-stone-400"}`}>{day.min}°</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
